import { useCallback, useMemo, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { GEDDocumento } from "../../../../common/domain/models/ged_documento";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { TelasIds } from "../../../../common/utils/tela_ids";
import {
  initialGedInternosFilterState,
  useGedInternosFilter,
} from "../hooks/GED_internos/filter";
import { useGedInternosPagination } from "../hooks/GED_internos/pagination";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { GedDocumentoForm } from "../schemas/GED_internos/ged_documento";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

import CommonConstants from "../../../../common/constants";

/**
 * LoadDocumentosState
 */

type LoadDocumentosState = {
  tipoPesquisa?: number;
  valueField?: string;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

export const useGedInternosViewModel = () => {
  const {
    reports: { getReportGedInternoUseCase },
    commons: {
      getUsuarioDropDownUseCase,
      getAllGedDocumentoUseCase,
      createGedDocumentoUseCase,
      updateGedDocumentoUseCase,
      deleteGedDocumentoUseCase,
      copyGedDocumentoUseCase,
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      gedTornarObsoletosUseCase,
      getGedArquivoDataUseCase,
      getGedArquivoPdfDataUseCase,
    },
  } = useDependencyInjection();

  const { loadCentroCustoDropDown, loadFuncionariosDropDown } =
    useDropDownLoader();

  const debouncedRef = useCreateDebounceHandler(750);

  const {
    pushErrorNotification,
    pushWarnNotification,
    pushInfoNotification,
    setPageSaving,
    setCustomLoading
  } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [openCadastroModal, setOpenCadastroModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openCopyModal, setOpenCopyModal] = useState(false);

  const [centroCustoDropDown, setCentroCustoDropDown] = useState<CentroCustoDropDown[]>();
  const [funcionariosDropDown, setFuncionariosDropDown] = useState<FuncionarioDropDown[]>();

  const [documentos, setDocumentos] = useState<GEDDocumento[]>();
  const [selectedDocumento, setSelectedDocumento] = useState<GEDDocumento>();

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (error) => pushErrorNotification("Erro", [error]),
  });

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const filters = useGedInternosFilter();
  const pagination = useGedInternosPagination();

  const funcionariosRevisadoDropdown = useMemo(() => {
    if (funcionariosDropDown === undefined) return null;
    return funcionariosDropDown.filter(x => {
      if (selectedDocumento) {
        if (x.idFuncionario === selectedDocumento.idFuncionarioRevisado) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    selectedDocumento,
    funcionariosDropDown,
  ]);

  const funcionariosAprovadoDropdown = useMemo(() => {
    if (funcionariosDropDown === undefined) return null;
    return funcionariosDropDown.filter(x => {
      if (selectedDocumento) {
        if (x.idFuncionario === selectedDocumento.idFuncionarioAprovado) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    selectedDocumento,
    funcionariosDropDown,
  ]);

  const funcionariosResponsavelDropdown = useMemo(() => {
    if (funcionariosDropDown === undefined) return null;
    return funcionariosDropDown.filter(x => {
      if (selectedDocumento) {
        if (x.idFuncionario === selectedDocumento.idFuncionarioResponsavel) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    selectedDocumento,
    funcionariosDropDown,
  ]);

  const funcionariosCopyDropDown = useMemo(() => {
    if (funcionariosDropDown === undefined) return null;
    return funcionariosDropDown.filter(x => x.canBeSelected);
  }, [
    funcionariosDropDown,
  ]);

  /**
   * Funções de carregamento
   */

  const loadUsuarioDropdown = useCallback(async () => {
    try {
      const usuariosDropdown =
        await getUsuarioDropDownUseCase.getUsuarioDropdown();
      useGedInternosFilter.setState({ usuariosDropdown });
    } catch (e: unknown) {
      useGedInternosFilter.setState({ usuariosDropdown: [] });
      throw e;
    }
  }, [getUsuarioDropDownUseCase]);

  const loadDocumentos = useCallback(
    async (funcState?: LoadDocumentosState) => {
      try {
        const filteredTipoPesquisa = evaluateParamValue(
          funcState?.tipoPesquisa,
          filters.tipoPesquisa,
        );
        const filteredValueField = evaluateParamValue(
          funcState?.valueField,
          filters.valueField,
        );
        const filteredStatus = evaluateParamValue(
          funcState?.status,
          filters.status,
        );

        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage =
          funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllGedDocumentoUseCase.getAllGedDocumento(
          TelasIds.GED_internos,
          CommonConstants.moduloDocumentos.Interno,
          filteredTipoPesquisa,
          filteredValueField,
          filteredStatus,
          filteredPage,
          filteredRowsPerPage,
        );

        const documentosAtualizados = getAllGedDocumentoUseCase.items.map((doc: any) => {
          const statusOriginal = Number(doc.revisao);
          const valorObsoleto = doc.obsoleto;

          if (statusOriginal === 1) {
            return { ...doc, Status: 3 };
          } else {
            return { ...doc, Status: valorObsoleto === true ? 2 : 1 };
          }
        });
        setDocumentos(documentosAtualizados);

        pagination.setTotalItems(getAllGedDocumentoUseCase.totalItems);
      } catch (e: unknown) {
        setDocumentos([]);
        throw e;
      }
    },
    [filters, pagination, getAllGedDocumentoUseCase],
  );

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadCentroCustoDropDown((dt) => setCentroCustoDropDown(dt));
      await loadFuncionariosDropDown((dt) => setFuncionariosDropDown(dt), { includeCantBeSelected: true });
      await loadUsuarioDropdown();
      await loadDocumentos();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadCentroCustoDropDown((dt) => setCentroCustoDropDown(dt));
      await loadFuncionariosDropDown((dt) => setFuncionariosDropDown(dt), { includeCantBeSelected: true });
      await loadUsuarioDropdown();
      await loadDocumentos();
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar documentos!"),
      );
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadUsuarioDropdown,
    loadDocumentos,
    pushErrorNotification,
  ]);

  const handleDocumentoSubmit = useCallback(
    async (form: GedDocumentoForm) => {
      try {
        setSaving(true);
        setCustomLoading(true);
        if (selectedDocumento) {
          await updateGedDocumentoUseCase.updateGedDocumentoInterno(
            TelasIds.GED_internos,
            selectedDocumento.idArquivoModulo,
            form.statusArquivo === 3 ? 1 : form.statusArquivo,
            form.tipo,
            form.arquivo,
            form.versao,
            form.centroCusto,
            form.revisadoPor,
            form.aprovadoPor,
            form.responsavelProcesso,
            form.localArmazenamento1,
            form.localArmazenamento2,
            form.acesso,
            form.recuperacao,
            form.protecao,
            form.retencao,
            form.disposicao,
            form.observacoes,
            form.file,
            form.filePdf,
            form.limparArquivo,
            form.limparArquivoPdf,
            form.statusArquivo === 3 ? 1 : 0,
          );
        } else {
          await createGedDocumentoUseCase.createGedDocumentoInterno(
            TelasIds.GED_internos,
            CommonConstants.moduloDocumentos.Interno,
            form.statusArquivo === 3 ? 1 : form.statusArquivo,
            form.tipo,
            form.arquivo,
            form.versao,
            form.centroCusto,
            form.revisadoPor,
            form.aprovadoPor,
            form.responsavelProcesso,
            form.localArmazenamento1,
            form.localArmazenamento2,
            form.acesso,
            form.recuperacao,
            form.protecao,
            form.retencao,
            form.disposicao,
            form.observacoes,
            form.file,
            form.filePdf,
            form.statusArquivo === 3 ? 1 : 0,
          );
        }
        await loadDocumentos();
        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
        setOpenCadastroModal(false);
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar documentos!"),
        );
      } finally {
        setSaving(false);
        setCustomLoading(false);
      }
    },
    [setCustomLoading, selectedDocumento, loadDocumentos, pushInfoNotification, updateGedDocumentoUseCase, createGedDocumentoUseCase, pushErrorNotification],
  );

  const handleDocumentoCopySubmit = useCallback(
    async (form: GedDocumentoForm) => {
      try {
        setSaving(true);

        if (selectedDocumento) {
          await copyGedDocumentoUseCase.copyGedDocumentoInterno(
            TelasIds.GED_internos,
            selectedDocumento.idArquivoModulo,
            form.statusArquivo,
            form.tipo,
            form.arquivo,
            form.versao,
            form.centroCusto,
            form.revisadoPor,
            form.aprovadoPor,
            form.responsavelProcesso,
            form.localArmazenamento1,
            form.localArmazenamento2,
            form.acesso,
            form.recuperacao,
            form.protecao,
            form.retencao,
            form.disposicao,
            form.observacoes,
            form.file,
            form.filePdf,
            form.limparArquivo,
            form.limparArquivoPdf,
          );
        }
        await loadDocumentos();
        pushInfoNotification("Sucesso", "Registro copiado com sucesso!");
        setOpenCopyModal(false);
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar documentos!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      selectedDocumento,
      copyGedDocumentoUseCase,
      loadDocumentos,
      pushErrorNotification,
      pushInfoNotification,
    ],
  );

  const handleReport = useCallback(async () => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportGedInternoUseCase.getReportGedInterno(
            requiredMimeType,
          ),
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar documentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    getReportGedInternoUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleCadastroDocumentoOpenState = useCallback((idArquivoModulo: number | null, openState: boolean) => {
    if (openState) {
      if (idArquivoModulo && documentos) {
        setSelectedDocumento(documentos.find((x) => x.idArquivoModulo === idArquivoModulo));
      } else {
        setSelectedDocumento(undefined);
      }
    }
    setOpenCadastroModal(openState);
  }, [
    documentos,
  ]);

  const handleDeleteDocumentoOpenState = useCallback(
    (idArquivoModulo: number | null, openState: boolean) => {
      if (openState) {
        if (idArquivoModulo && documentos) {
          setSelectedDocumento(
            documentos.find((x) => x.idArquivoModulo === idArquivoModulo),
          );
        } else {
          setSelectedDocumento(undefined);
        }
      }
      setOpenDeleteModal(openState);
    },
    [documentos],
  );

  const handleConfirmarDeleteDocumento = useCallback(async () => {
    if (selectedDocumento) {
      try {
        setSaving(true);
        await deleteGedDocumentoUseCase.deleteGedDocumento(
          TelasIds.GED_internos,
          selectedDocumento.idArquivoModulo,
        );
        await loadDocumentos();
        pushInfoNotification("Sucesso", "Registro excluido com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar documentos!"),
        );
      } finally {
        setSaving(false);
        setOpenDeleteModal(false);
      }
    }
  }, [
    selectedDocumento,
    deleteGedDocumentoUseCase,
    pushErrorNotification,
    loadDocumentos,
    pushInfoNotification,
  ]);

  const handleCopyDocumentoOpenState = useCallback(
    (idArquivoModulo: number | null, openState: boolean) => {
      if (openState) {
        if (idArquivoModulo && documentos) {
          setSelectedDocumento(
            documentos.find((x) => x.idArquivoModulo === idArquivoModulo),
          );
        } else {
          setSelectedDocumento(undefined);
        }
      }
      setOpenCopyModal(openState);
    },
    [documentos],
  );

  const handleDownloadFile = useCallback(async () => {
    if (selectedDocumento && selectedDocumento.nome) {
      try {
        setCustomLoading(true);
        await getGedArquivoDataUseCase
          .getGedArquivoData(selectedDocumento.idArquivoModulo, selectedDocumento.nome);
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar documentos!"),
        );
      } finally {
        setCustomLoading(false);
      }
    }
  }, [
    selectedDocumento,
    getGedArquivoDataUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleDownloadFilePdf = useCallback(async () => {
    if (selectedDocumento && selectedDocumento.nomePdf) {
      try {
        setCustomLoading(true);
        await getGedArquivoPdfDataUseCase
          .getGedArquivoPdfData(selectedDocumento.idArquivoModulo, selectedDocumento.nomePdf);
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar documentos!"),
        );
      } finally {
        setCustomLoading(false);
      }
    }
  }, [
    selectedDocumento,
    getGedArquivoPdfDataUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleTipoPesquisaChange = useDebouncedCallback(
    debouncedRef,
    async (tipoPesquisa: number) => {
      useGedInternosFilter.setState({ tipoPesquisa, valueField: "" });
      return async () => {
        try {
          setLoading(true);
          useGedInternosPagination.getState().setPage(1);
          await loadDocumentos({ tipoPesquisa, valueField: "", page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar documentos!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [pushErrorNotification, loadDocumentos],
  );

  const handleValueFieldChange = useDebouncedCallback(
    debouncedRef,
    async (valueField: string) => {
      useGedInternosFilter.setState({ valueField });
      return async () => {
        try {
          setLoading(true);
          useGedInternosPagination.getState().setPage(1);
          await loadDocumentos({ valueField, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar documentos!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [pushErrorNotification, loadDocumentos],
  );

  const handleStatusChange = useDebouncedCallback(
    debouncedRef,
    async (status: number) => {
      useGedInternosFilter.setState({ status });
      return async () => {
        try {
          setLoading(true);
          useGedInternosPagination.getState().setPage(1);
          await loadDocumentos({ status, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar documentos!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [pushErrorNotification, loadDocumentos],
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useGedInternosFilter.getState().clearFilters();
      useGedInternosPagination.getState().setPage(1);
      await loadDocumentos({ ...initialGedInternosFilterState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar documentos!"),
      );
    } finally {
      setLoading(false);
    }
  }, [loadDocumentos, pushErrorNotification]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useGedInternosPagination.setState({ page: page + 1 });
        await loadDocumentos({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar documentos!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadDocumentos, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useGedInternosPagination.setState({ page: 1, rowsPerPage });
        await loadDocumentos({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar documentos!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadDocumentos, pushErrorNotification],
  );

  const handleRowSelectionModel = useCallback(
    async (model: GridRowSelectionModel) => {
      setRowSelectionModel(model);
    },
    [],
  );

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedDocumento) {
      changeHistory.handleOpenChangeHistory(
        selectedDocumento.idArquivoModulo,
        LogTipoTabela.ARQUIVO_MODULO,
        false,
      );
    }
  }, [selectedDocumento, changeHistory]);

  const handleActionVisualize = useCallback(
    async (idArquivoModulo: number) => {
      if (documentos && documentos.length > 0) {
        const arquivoModulo = documentos.find(
          (x) => x.idArquivoModulo === idArquivoModulo,
        );
        if (arquivoModulo && arquivoModulo.nomePdf) {
          try {
            setLoading(true);
            await getGedArquivoPdfDataUseCase.getGedArquivoPdfData(
              arquivoModulo.idArquivoModulo,
              arquivoModulo.nomePdf,
            );
          } catch (e: unknown) {
            pushErrorNotification(
              "Erro",
              processError(e, "Falha ao buscar documentos!"),
            );
          } finally {
            setLoading(false);
          }
        }
      }
    },
    [documentos, getGedArquivoPdfDataUseCase, pushErrorNotification],
  );

  const handleActionDownload = useCallback(
    async (idArquivoModulo: number) => {
      setCustomLoading(true);
      if (documentos && documentos.length > 0) {
        const arquivoModulo = documentos.find(
          (x) => x.idArquivoModulo === idArquivoModulo,
        );
        if (arquivoModulo && arquivoModulo.nome) {
          try {
            setLoading(true);
            await getGedArquivoDataUseCase.getGedArquivoData(
              arquivoModulo.idArquivoModulo,
              arquivoModulo.nome,
            );
          } catch (e: unknown) {
            pushErrorNotification(
              "Erro",
              processError(e, "Falha ao buscar documentos!"),
            );
          } finally {
            setLoading(false);
            setCustomLoading(false);
          }
        }
      }
    },
    [documentos, getGedArquivoDataUseCase, pushErrorNotification, setCustomLoading],
  );

  const handleTornarObsoleto = useCallback(async () => {
    try {
      if (!rowSelectionModel || rowSelectionModel.ids.size === 0) {
        pushWarnNotification(
          "Alerta",
          "Você deve selecionar pelo menos 1 item!",
        );
        return;
      }

      if (window.confirm("Você deseja tornar estes itens obsoletos?")) {
        setSaving(true);
        setPageSaving(true);

        await gedTornarObsoletosUseCase.gedTornarObsoleto(
          rowSelectionModel.ids,
        );

        await loadDocumentos();

        pushInfoNotification("Sucesso", "Registros salvos com sucesso!");

        setRowSelectionModel({ type: "include", ids: new Set() });
      }
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao atualizar registros!"),
      );
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    rowSelectionModel,
    gedTornarObsoletosUseCase,
    loadDocumentos,
    pushInfoNotification,
    pushErrorNotification,
    pushWarnNotification,
    setPageSaving,
  ]);

  return {
    loading,
    saving,
    openCadastroModal,
    openDeleteModal,
    openCopyModal,
    errorMessage,
    centroCustoDropDown: centroCustoDropDown ?? [],
    funcionariosRevisadoDropdown: funcionariosRevisadoDropdown ?? [],
    funcionariosAprovadoDropdown: funcionariosAprovadoDropdown ?? [],
    funcionariosResponsavelDropdown: funcionariosResponsavelDropdown ?? [],
    funcionariosCopyDropDown: funcionariosCopyDropDown ?? [],
    documentos: documentos ?? [],
    selectedDocumento: selectedDocumento ?? null,
    rowSelectionModel,
    changeHistory,
    reportViewerState,
    filters,
    pagination,
    handleDocumentoSubmit,
    handleDocumentoCopySubmit,
    handleReport,
    handleReload,
    handleCadastroDocumentoOpenState,
    handleDeleteDocumentoOpenState,
    handleConfirmarDeleteDocumento,
    handleCopyDocumentoOpenState,
    handleDownloadFile,
    handleDownloadFilePdf,
    handleTipoPesquisaChange,
    handleValueFieldChange,
    handleStatusChange,
    handleClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleRowSelectionModel,
    handleOpenChangeHistory,
    handleActionVisualize,
    handleActionDownload,
    handleTornarObsoleto,
  };
};
